import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Insight } from "@stratify/shared";

export interface InsightsState {
  insightsByDocument: { [documentId: string]: Insight[] };
  loading: boolean;
  error: string | null;
}

const initialState: InsightsState = {
  insightsByDocument: {},
  loading: false,
  error: null,
};

const insightsSlice = createSlice({
  name: "insights",
  initialState,
  reducers: {
    fetchInsightsStart: (state, _action: PayloadAction<string>) => {
      state.loading = true;
      state.error = null;
    },
    fetchInsightsSuccess: (
      state,
      action: PayloadAction<{ documentId: string; insights: Insight[] }>
    ) => {
      state.loading = false;
      state.insightsByDocument[action.payload.documentId] =
        action.payload.insights;
    },
    fetchInsightsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    createInsightStart: (
      state,
      _action: PayloadAction<{
        documentId: string;
        userId: string;
        text: string;
        page?: number;
        metadata?: Record<string, unknown>;
      }>
    ) => {
      state.loading = true;
      state.error = null;
    },
    createInsightSuccess: (state, action: PayloadAction<Insight>) => {
      state.loading = false;
      const documentId = action.payload.documentId;
      if (!state.insightsByDocument[documentId]) {
        state.insightsByDocument[documentId] = [];
      }
      state.insightsByDocument[documentId].push(action.payload);
    },
    createInsightFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    updateInsightStart: (
      state,
      _action: PayloadAction<{
        documentId: string;
        insightId: string;
        updates: Partial<
          Omit<Insight, "id" | "documentId" | "createdAt" | "updatedAt">
        >;
      }>
    ) => {
      state.loading = true;
      state.error = null;
    },
    updateInsightSuccess: (
      state,
      action: PayloadAction<{
        documentId: string;
        insightId: string;
        updates: Partial<
          Omit<Insight, "id" | "documentId" | "createdAt" | "updatedAt">
        >;
      }>
    ) => {
      state.loading = false;
      const { documentId, insightId, updates } = action.payload;
      const insights = state.insightsByDocument[documentId];
      if (insights) {
        const index = insights.findIndex((insight) => insight.id === insightId);
        if (index !== -1) {
          insights[index] = { ...insights[index], ...updates };
        }
      }
    },
    updateInsightFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    deleteInsightStart: (
      state,
      _action: PayloadAction<{ documentId: string; insightId: string }>
    ) => {
      state.loading = true;
      state.error = null;
    },
    deleteInsightSuccess: (
      state,
      action: PayloadAction<{ documentId: string; insightId: string }>
    ) => {
      state.loading = false;
      const { documentId, insightId } = action.payload;
      const insights = state.insightsByDocument[documentId];
      if (insights) {
        state.insightsByDocument[documentId] = insights.filter(
          (insight) => insight.id !== insightId
        );
      }
    },
    deleteInsightFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchSingleInsightStart: (state, _action: PayloadAction<string>) => {
      state.loading = true;
      state.error = null;
    },
    fetchSingleInsightSuccess: (state, action: PayloadAction<Insight>) => {
      state.loading = false;
      const documentId = action.payload.documentId;
      if (!state.insightsByDocument[documentId]) {
        state.insightsByDocument[documentId] = [];
      }
      // Avoid duplicates
      if (
        !state.insightsByDocument[documentId].find(
          (i) => i.id === action.payload.id
        )
      ) {
        state.insightsByDocument[documentId].push(action.payload);
      }
    },
    fetchSingleInsightFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

export const {
  fetchInsightsStart,
  fetchInsightsSuccess,
  fetchInsightsFailure,
  createInsightStart,
  createInsightSuccess,
  createInsightFailure,
  updateInsightStart,
  updateInsightSuccess,
  updateInsightFailure,
  deleteInsightStart,
  deleteInsightSuccess,
  deleteInsightFailure,
  fetchSingleInsightStart,
  fetchSingleInsightSuccess,
  fetchSingleInsightFailure,
} = insightsSlice.actions;

export default insightsSlice.reducer;
